import { App } from "@capacitor/app"
import type { PluginListenerHandle } from "@capacitor/core"
import { backButton } from "@telegram-apps/sdk-react"
import { useEffect, useSyncExternalStore } from "react"
import {
	getActiveHandler,
	getSnapshot,
	subscribe,
} from "@/shared/lib/back-button"
import { useExitApp, usePlatform } from "@/shared/lib/platform"

/**
 * Universal back button manager. Mount once in the root layout.
 *
 * - Telegram: manages native back button (show/hide + click listener), renders nothing
 * - Android: bridges the hardware/gesture back event into the handler stack
 * - iOS/browser: renders nothing — UI is delegated to BackButton
 */
export function BackButtonManager() {
	const platform = usePlatform()
	const hasHandlers = useSyncExternalStore(subscribe, getSnapshot) > 0

	if (platform === "telegram") {
		return <TelegramBackButtonBridge hasHandlers={hasHandlers} />
	}

	if (platform === "android") {
		return <CapacitorBackButtonBridge />
	}

	return null
}

/**
 * Separate component so Telegram SDK hooks only run on Telegram platform.
 * Manages the native back button visibility and click handling.
 */
function TelegramBackButtonBridge({ hasHandlers }: { hasHandlers: boolean }) {
	useEffect(() => {
		if (hasHandlers) {
			backButton.show.ifAvailable()
		} else {
			backButton.hide.ifAvailable()
		}
	}, [hasHandlers])

	useEffect(() => {
		if (!backButton.onClick.isAvailable()) return
		return backButton.onClick(() => {
			getActiveHandler()?.()
		})
	}, [])

	return null
}

/**
 * Routes the Android hardware/gesture back event into the shared handler stack.
 * Registering a backButton listener disables the default WebView history behaviour,
 * so we reproduce it manually when no handler is registered.
 */
function CapacitorBackButtonBridge() {
	const exitApp = useExitApp()

	useEffect(() => {
		let handle: PluginListenerHandle | undefined
		let cancelled = false

		App.addListener("backButton", ({ canGoBack }) => {
			const handler = getActiveHandler()
			if (handler) {
				handler()
			} else if (canGoBack) {
				window.history.back()
			} else {
				exitApp()
			}
		}).then((h) => {
			if (cancelled) h.remove()
			else handle = h
		})

		return () => {
			cancelled = true
			handle?.remove()
		}
	}, [exitApp])

	return null
}
